/**
 * client/src/lib/api.ts — the one place a fetch becomes either a typed value or a typed refusal.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE PARSES INSTEAD OF CASTING
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * Every sibling repo's client does `return res.json() as Promise<T>` and it is fine there. It is not
 * fine here, and `shared/protocol.ts` says why in as many words:
 *
 *   **VOX-LAW 14. Absent ≠ zero on the wire. A missing numeric field is a hard failure, never
 *   coerced.** "a missing `noiseFloorDb` coerced to 0 is a room floor of 0 dBFS, which makes
 *   `thresholdDbFor()` return +12 dB, which makes every detector downstream report a take of pure
 *   silence — with no error anywhere, and with the recording of somebody's mother sitting perfectly
 *   intact on disk behind it."
 *
 * A cast is the coercion, one level up: `as SessionView` on a body whose `coachSpent` the server
 * forgot to send produces `undefined`, which renders as "NaN of 2 corrections used" on `/live` and
 * as nothing at all on the family path. So every reader below goes through `num`/`str`/`bool`/
 * `enumOf`/`optNum` from `shared/protocol.ts`, which throw with the field path in the message.
 *
 * The cost is this file being long. The benefit is that a contract drift between cloud-2 and the
 * browser surfaces as `WireError: takes[2].thresholdDb: expected a finite number, got undefined` in
 * one place, instead of as a wrong picture on a screen somebody's mother is looking at.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT A THROWN READER MEANS ON THE FAMILY PATH
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * **VOX-LAW 16b. No exception, assertion, or protocol failure anywhere in `client/src/studio/` may
 * render as an error to the person recording.** This file is not under `studio/`, but every family
 * page calls it, so the same rule binds: a `WireError` from here degrades at the call site to
 * `FAMILY.mishap.keepGoing` or to the deferred path, and reports itself to `/live`. It never becomes
 * a sentence on her screen. `describeForConsole()` at the bottom is the *other* end of that: the
 * full detail, for Karti, in the one place a stack trace is the right answer (§3.15).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ROUTES THIS FILE ADDS TO DESIGN §14, AND WHY EACH ONE EXISTS
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * §14's list is the API surface as designed, and three things the copy in `shared/voice/copy.ts`
 * requires are not on it. They are named here rather than improvised at four call sites:
 *
 *   1. `GET  /api/v1/sessions/:id/reveal` → `{ text, audioUrl, ms }`.
 *      `SessionView` deliberately does NOT carry `revealText`, and that is the point: the studio
 *      polls `SessionView` *while she is still recording*, and §3.13 says "She never sees it." A
 *      separate route that answers only once the session has reached `revealed` makes that
 *      structural instead of a rule four components have to remember not to break.
 *   2. `DELETE /api/v1/sessions/:id/reveal`.
 *      §3.13: a "no — delete that" button "that actually deletes, immediately, the audio and the
 *      row." A button with no route behind it is the worst possible version of that promise.
 *   3. `POST /api/v1/sessions/:id/claim-link/email` → 204.
 *      VOX-LAW 20 and `FAMILY.keep.linkEmailLabel`: "an 'email this to yourself' field". The token
 *      is in the request path's session, never in the request body, so nothing here can email a
 *      claim link for a session the caller cannot already see.
 *   4. `GET  /api/v1/sessions/:id/links` → `{ claimUrl, claimToken, liveUrl }`.
 *      `CreateSessionResponse` carries the claim token exactly once, at creation. **A durable handle
 *      that exists only in the tab that minted it is not durable** — and the screen that prints it
 *      is the single most reloaded screen in the product: she photographs it, the laptop sleeps, the
 *      tab restores, somebody hits refresh because the page "looked stuck". VOX-LAW 26 says an
 *      unclaimed session is never swept, so the token is on the server forever; this is the route
 *      that asks for it back. `readAccess` decides and refuses 404 (VOX-LAW 18).
 *
 * And one correction to a route that IS on the list. §14 says `GET /api/v1/sessions/:id` is
 * resolvable "(actor or claim token)" without saying how the token is presented. **This file used to
 * say it was presented as the id** — `vox_` and `ses_` being distinguishable by prefix — and that was
 * wrong about the server, which refuses anything that is not a `ses_` id before it reaches SQLite.
 * The token is presented as a **credential**: `GET /c/:claimToken` to resolve it once, then
 * `x-vox-claim` on everything after. `resolveSession()` at the bottom of this file is that sequence,
 * and it is the only correct one — see its header for why the order is the server's to dictate.
 *
 * ⚠️ **`GET /c/:claimToken` is a SERVER route, not a page.** `claimView()` below is the `fetch` form
 * of it: the same route answers the SPA shell to a navigation and JSON to an explicit
 * `Accept: application/json` (`vite.config.ts` proxies `^/c/` for exactly this reason, with the
 * trailing slash anchored — see the scar recorded there).
 */

import type { Actor, ActorKind } from "../../../shared/actor.ts";
import type {
  ApiError as ApiErrorBody,
  BrowserMetrics,
  ClaimView,
  CompleteResponse,
  ConsentState,
  CreateSessionRequest,
  CreateSessionResponse,
  HealthReport,
  IneligibleReason,
  LiveEvent,
  SessionEnvelope,
  SessionLinks,
  SessionState,
  SessionView,
  StatusReport,
  TakeSummary,
  VoiceDetail,
  VoiceRevSummary,
  VoiceStatus,
  VoiceSummary,
} from "../../../shared/protocol.ts";
import {
  CONSENT_STATES,
  SESSION_STATES,
  TAKE_KINDS,
  VOICE_STATUSES,
  WireError,
  bool,
  enumOf,
  num,
  optNum,
  optStr,
  str,
} from "../../../shared/protocol.ts";
import { SEED_KINDS, idKindOf } from "../../../shared/voice/keys.ts";
import type { SeedKind } from "../../../shared/voice/keys.ts";
import type { SessionShape } from "../../../shared/voice/script.ts";

const BASE = "/api/v1";

const SESSION_SHAPES: readonly SessionShape[] = ["short", "long"];
const ACTOR_KINDS: readonly ActorKind[] = ["guest", "user"];

// ─── refusals ────────────────────────────────────────────────────────────────

/**
 * A non-2xx, a network failure, or a body that was not JSON.
 *
 * Named `ApiError` like the class in every sibling; `shared/protocol.ts`'s `ApiError` is the *wire
 * envelope* `{code, detail, sessionId?}` and is imported above as `ApiErrorBody`. Two names for two
 * things, because they genuinely are two things: one is what the server said, one is what the fetch
 * did.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** The server's `code` verbatim — a machine string like `session-not-found`, never prose. */
    readonly code: string,
    /** The server's `detail`. **For Karti and for `/live`. Never rendered on the family path.** */
    readonly detail: string,
    /** Present when the failure belongs to a session, so `/live` and the push to Karti join up. */
    readonly sessionId?: string,
  ) {
    super(`${status} ${code}: ${detail}`);
    this.name = "ApiError";
  }

  /**
   * The request never got an answer. Distinct from a 5xx and the UI treats it differently: "the
   * laptop lost the wifi" and "the server is broken" send Karti to two different rooms, and on the
   * family path the first one is recoverable by waiting while the second is not.
   */
  get offline(): boolean {
    return this.status === 0;
  }

  /**
   * **VOX-LAW 18: routes refuse 404, never 403.** So a 404 means "no, or not for you" and nothing
   * in this client may distinguish the two — a 403 on a voice id is a statement that the voice
   * exists, and on this site that is a statement about whose recordings are on this server.
   */
  get notFound(): boolean {
    return this.status === 404;
  }

  /** VOX-LAW 21's shape at the UI layer: a throttled auth provider is never spelled "wrong password". */
  get upstreamDown(): boolean {
    return this.status === 503;
  }
}

// ─── the fetch ───────────────────────────────────────────────────────────────

interface Options {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * A claim token to present on this call, sent as `x-vox-claim`.
   *
   * The helper watching `/live/:token` is on another laptop in another city. He holds no cookie for
   * this session and is not its actor, so `readAccess` refuses him everything until he presents the
   * token — see `resolveSession()` for the order the server actually requires.
   */
  claim?: string | null;
  /**
   * Send `path` as given instead of under `/api/v1`. Exactly one route needs it: `GET /c/:claimToken`
   * lives at the root because it is a link a family keeps, and a durable handle reading
   * `voice.karti.ai/api/v1/…` is a handle that looks like a mistake to the person holding it.
   */
  root?: boolean;
}

/**
 * `credentials: "same-origin"` and nothing else. The session is the signed httpOnly cookie minted by
 * `GET /api/v1/session` — **VOX-LAW 20: the only route that sets one** — so there is no token to
 * attach and nothing for XSS to read.
 *
 * ⚠️ There is deliberately no retry here. `lib/session.tsx` retries the *boot* request once, because
 * concluding "signed out" from a transport blip signs somebody out of a year-long session; every
 * other call is either idempotent and safe for the caller to repeat, or it is
 * `POST /api/v1/sessions/:id/takes` — and a blind retry of an upload is how one recording becomes
 * two rows with the same `promptId` and the same `attempt`.
 */
async function request<T>(path: string, parse: (body: unknown) => T, opts: Options = {}): Promise<T> {
  const method = opts.method ?? "GET";
  let res: Response;
  try {
    const headers: Record<string, string> = {
      /**
       * ⚠️ **Load-bearing on `GET /c/:claimToken`, and only there.** That route answers a payload on
       * an explicit `application/json` and the SPA shell on anything else, deliberately: a top-level
       * navigation from Mail or WhatsApp must get a page, and a wildcard `Accept` from an in-app
       * webview must not get a wall of JSON. Omitting this header here would make `resolveSession()`
       * parse `index.html` and report "not JSON" for a link that is working perfectly.
       */
      accept: "application/json",
    };
    if (opts.body !== undefined) headers["content-type"] = "application/json";
    // `CLAIM_HEADER` in `server/src/access.ts`. A header rather than `?claim=`: a query parameter
    // survives into referrers, proxy logs and the browser's own history, and this is a credential.
    if (opts.claim) headers["x-vox-claim"] = opts.claim;

    res = await fetch(opts.root === true ? path : BASE + path, {
      method,
      credentials: "same-origin",
      headers: Object.keys(headers).length === 0 ? undefined : headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (cause) {
    // An abort is the caller's own decision and must not be dressed up as a network failure —
    // `<Wait>` and `<Live>` both abort in-flight requests on unmount, and turning that into an
    // "offline" state would render a notice on a screen that no longer exists.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "unreachable", `${method} ${path}: ${String(cause)}`);
  }

  if (res.status === 204) return parse(undefined);

  const text = await res.text();
  let body: unknown;
  try {
    body = text === "" ? undefined : JSON.parse(text);
  } catch {
    // A body that is not JSON on a 2xx means something in front of the app answered — Caddy, a
    // captive portal, or `@fastify/static`'s SPA fallback serving index.html because the route
    // does not exist. Saying so beats "Unexpected token < in JSON at position 0".
    throw new ApiError(
      res.ok ? 502 : res.status,
      "not-json",
      `${method} ${path} answered ${res.status} with ${text.length} bytes that were not JSON`,
    );
  }

  if (!res.ok) throw refusal(res.status, body, `${method} ${path}`);
  return parse(body);
}

/** The server speaks `ApiError` from `shared/protocol.ts`. Anything else gets a legible stand-in. */
function refusal(status: number, body: unknown, where: string): ApiError {
  const wire = body as Partial<ApiErrorBody> | undefined;
  const code = typeof wire?.code === "string" && wire.code !== "" ? wire.code : `http-${status}`;
  const detail = typeof wire?.detail === "string" && wire.detail !== "" ? wire.detail : `${where} failed`;
  const sessionId = typeof wire?.sessionId === "string" ? wire.sessionId : undefined;
  return new ApiError(status, code, detail, sessionId);
}

/** Every reader takes `unknown` and gets an object or throws. One place, one message shape. */
function obj(v: unknown, path: string): Record<string, unknown> {
  if (v === null || typeof v !== "object" || Array.isArray(v)) {
    throw new WireError(`${path}: expected an object, got ${JSON.stringify(v)}`);
  }
  return v as Record<string, unknown>;
}

function arr(v: unknown, path: string): unknown[] {
  if (!Array.isArray(v)) throw new WireError(`${path}: expected an array, got ${JSON.stringify(v)}`);
  return v;
}

function strings(o: Record<string, unknown>, key: string, path: string): string[] {
  return arr(o[key], `${path}.${key}`).map((v, i) => {
    if (typeof v !== "string") {
      throw new WireError(`${path}.${key}[${i}]: expected a string, got ${JSON.stringify(v)}`);
    }
    return v;
  });
}

const nothing = (): void => undefined;

// ─── readers ─────────────────────────────────────────────────────────────────

export function readActor(v: unknown, path = "actor"): Actor {
  const o = obj(v, path);
  const kind = enumOf(o, "kind", ACTOR_KINDS, path);
  return {
    kind,
    id: str(o, "id", path),
    email: optStr(o, "email", path) ?? undefined,
    handle: optStr(o, "handle", path) ?? undefined,
    // `bool()` and not `?? false`: `isAdmin` is derived live from `users.email ∈ VOX_ADMIN_EMAILS`
    // per request, and a coerced `false` on a dropped field silently hides `/admin` from Karti while
    // looking exactly like a working page.
    isAdmin: bool(o, "isAdmin", path),
  };
}

export function readSessionEnvelope(v: unknown): SessionEnvelope {
  const o = obj(v, "session");
  const app = obj(o["app"], "session.app");
  return {
    actor: readActor(o["actor"], "session.actor"),
    app: {
      publicOrigin: str(app, "publicOrigin", "session.app"),
      // `bool()`, deliberately. `ttsConfigured: false` is a SUPPORTED PRODUCT STATE (§3.14 — the
      // deferred path is the common path), and "the field is missing" must not silently become it:
      // the difference is whether the studio offers her the deferred choice up front or discovers
      // it at the end, and discovering it at the end is the failure the whole section exists to
      // prevent.
      ttsConfigured: bool(app, "ttsConfigured", "session.app"),
      helperName: str(app, "helperName", "session.app"),
    },
  };
}

export function readHealth(v: unknown, path = "health"): HealthReport | null {
  if (v === null || v === undefined) return null;
  const o = obj(v, path);
  return {
    modelLoaded: bool(o, "modelLoaded", path),
    dtype: optStr(o, "dtype", path),
    variants: strings(o, "variants", path),
    rttMs: num(o, "rttMs", path),
    raw: obj(o["raw"], `${path}.raw`),
  };
}

function readReach(v: unknown, path: string): { reachable: boolean; rttMs: number | null } {
  const o = obj(v, path);
  return { reachable: bool(o, "reachable", path), rttMs: optNum(o, "rttMs", path) };
}

export function readStatus(v: unknown): StatusReport {
  const o = obj(v, "status");
  const disk = obj(o["disk"], "status.disk");
  const queueRaw = obj(o["queue"], "status.queue");
  const queue: Record<string, { queued: number; running: number }> = {};
  for (const [lane, cell] of Object.entries(queueRaw)) {
    const c = obj(cell, `status.queue.${lane}`);
    queue[lane] = { queued: num(c, "queued", `status.queue.${lane}`), running: num(c, "running", `status.queue.${lane}`) };
  }
  return {
    ok: bool(o, "ok", "status"),
    env: enumOf(o, "env", ["dev", "prod"] as const, "status"),
    tts: readHealth(o["tts"], "status.tts"),
    asr: readReach(o["asr"], "status.asr"),
    origin: readReach(o["origin"], "status.origin"),
    queue,
    disk: {
      dataDirFreeBytes: num(disk, "dataDirFreeBytes", "status.disk"),
      // Null and not zero: amd-server holds `/mnt/storage` and cloud-2 cannot see it, so "we did not
      // ask" and "the disk is full" are different facts and §12.2 is the reason the second one
      // matters. `optNum` returns null; `Number(null)` would have returned 0.
      storageDirFreeBytes: optNum(disk, "storageDirFreeBytes", "status.disk"),
    },
    warnings: strings(o, "warnings", "status"),
    payoff: readPayoff(o["payoff"]),
  };
}

/**
 * §3.13's input and §3.7's prompt recordings, off the wire.
 *
 * Read rather than cast, like everything else here (VOX-LAW 14): `sessionsWaitingOnASentence`
 * coerced from `undefined` to 0 would say *"nothing is stuck"* on the one page whose job is to say
 * what is, which is the same class of failure as the missing `noiseFloorDb` in this file's header.
 */
function readPayoff(v: unknown): StatusReport["payoff"] {
  const o = obj(v, "status.payoff");
  const audio = obj(o["promptAudio"], "status.payoff.promptAudio");
  return {
    sessionsWaitingOnASentence: num(o, "sessionsWaitingOnASentence", "status.payoff"),
    sessionsRevealed: num(o, "sessionsRevealed", "status.payoff"),
    promptAudio: {
      recorded: num(audio, "recorded", "status.payoff.promptAudio"),
      expected: num(audio, "expected", "status.payoff.promptAudio"),
      complete: bool(audio, "complete", "status.payoff.promptAudio"),
    },
  };
}

function readBitDepth(o: Record<string, unknown>, path: string): 16 | 24 {
  const v = num(o, "bitDepth", path);
  if (v !== 16 && v !== 24) {
    throw new WireError(`${path}.bitDepth: expected 16 or 24, got ${v}`);
  }
  return v;
}

export function readTake(v: unknown, path: string): TakeSummary {
  const o = obj(v, path);
  return {
    id: str(o, "id", path),
    promptId: str(o, "promptId", path),
    attempt: num(o, "attempt", path),
    kind: enumOf(o, "kind", TAKE_KINDS, path),
    durationMs: num(o, "durationMs", path),
    bytes: num(o, "bytes", path),
    bitDepth: readBitDepth(o, path),
    sampleRate: num(o, "sampleRate", path),
    // ⚠️ `optNum`, and this is the exact field VOX-LAW 14's header uses as its example. Null until
    // the worker has measured it; zero would mean "speech starts at sample 0".
    onsetMs: optNum(o, "onsetMs", path),
    tailMs: optNum(o, "tailMs", path),
    thresholdDb: num(o, "thresholdDb", path),
    seedEligible: bool(o, "seedEligible", path),
    ineligibleReason: optStr(o, "ineligibleReason", path) as IneligibleReason | null,
    voidedReason: optStr(o, "voidedReason", path) as TakeSummary["voidedReason"],
    createdAt: num(o, "createdAt", path),
  };
}

export function readSessionView(v: unknown): SessionView {
  const o = obj(v, "session");
  return {
    id: str(o, "id", "session"),
    state: enumOf(o, "state", SESSION_STATES, "session"),
    subjectName: str(o, "subjectName", "session"),
    seedKind: enumOf(o, "seedKind", SEED_KINDS, "session"),
    shape: enumOf(o, "shape", SESSION_SHAPES, "session"),
    scriptVersion: num(o, "scriptVersion", "session"),
    plan: strings(o, "plan", "session"),
    takes: arr(o["takes"], "session.takes").map((t, i) => readTake(t, `session.takes[${i}]`)),
    coachSpent: num(o, "coachSpent", "session"),
    createdAt: num(o, "createdAt", "session"),
    completedAt: optNum(o, "completedAt", "session"),
    revealMs: optNum(o, "revealMs", "session"),
    health: readHealth(o["health"], "session.health"),
  };
}

export function readCreateSession(v: unknown): CreateSessionResponse {
  const o = obj(v, "created");
  return {
    sessionId: str(o, "sessionId", "created"),
    claimToken: str(o, "claimToken", "created"),
    liveUrl: str(o, "liveUrl", "created"),
    health: readHealth(o["health"], "created.health"),
  };
}

export function readVoiceSummary(v: unknown, path: string): VoiceSummary {
  const o = obj(v, path);
  return {
    id: str(o, "id", path),
    label: str(o, "label", path),
    seedKind: enumOf(o, "seedKind", SEED_KINDS, path),
    status: enumOf(o, "status", VOICE_STATUSES, path) as VoiceStatus,
    consentState: enumOf(o, "consentState", CONSENT_STATES, path) as ConsentState,
    ownerUserId: optStr(o, "ownerUserId", path),
    currentRev: optStr(o, "currentRev", path),
    createdAt: num(o, "createdAt", path),
  };
}

export function readVoiceRev(v: unknown, path: string): VoiceRevSummary {
  const o = obj(v, path);
  return {
    rev: str(o, "rev", path),
    remoteId: str(o, "remoteId", path),
    hostUrl: str(o, "hostUrl", path),
    seedSeconds: num(o, "seedSeconds", path),
    seedBytes: num(o, "seedBytes", path),
    // §8.5 — a DISTRIBUTION, not a number, and all three parts are null together until the canary
    // has rendered anything. Against 13.3–17.8% run-to-run variance a single value is either noisy
    // or blind, so a reader that coerced these to 0 would draw a canary baseline of "perfect".
    simNormMean: optNum(o, "simNormMean", path),
    simNormSd: optNum(o, "simNormSd", path),
    simNormRenders: optNum(o, "simNormRenders", path),
    publishedAt: optNum(o, "publishedAt", path),
    withdrawnAt: optNum(o, "withdrawnAt", path),
    withdrawnReason: optStr(o, "withdrawnReason", path),
  };
}

export function readVoiceDetail(v: unknown): VoiceDetail {
  const o = obj(v, "voice");
  return {
    ...readVoiceSummary(v, "voice"),
    ownerUserId: optStr(o, "ownerUserId", "voice"),
    sessionId: optStr(o, "sessionId", "voice"),
    revs: arr(o["revs"], "voice.revs").map((r, i) => readVoiceRev(r, `voice.revs[${i}]`)),
    consentWer: optNum(o, "consentWer", "voice"),
  };
}

/**
 * `GET /api/v1/sessions/:id/reveal` — see the header for why this is not a field on `SessionView`.
 *
 * `audioUrl` is a path the server chose, never a blob sha this client assembled: **VOX-LAW 19,
 * there is no `GET /blob/:sha`, blob shas are never addressable from outside.**
 */
export interface RevealView {
  /** §3.13 — the sentence the OTHER person wrote. On screen as it plays, so she is never surprised. */
  text: string;
  audioUrl: string;
  /** Last recording → first audio, ms. §3.12's budget is 75 000 p90 and a breach is a defect. */
  ms: number | null;
}

export function readReveal(v: unknown): RevealView {
  const o = obj(v, "reveal");
  return { text: str(o, "text", "reveal"), audioUrl: str(o, "audioUrl", "reveal"), ms: optNum(o, "ms", "reveal") };
}

export function readLinks(v: unknown): SessionLinks {
  const o = obj(v, "links");
  return {
    claimUrl: str(o, "claimUrl", "links"),
    claimToken: str(o, "claimToken", "links"),
    liveUrl: str(o, "liveUrl", "links"),
  };
}

/**
 * `POST /api/v1/sessions/:id/complete`.
 *
 * ⚠️ **This used to parse the answer as a `SessionView` and threw on every call.** The route answers a
 * `CompleteResponse` — no `id`, no `subjectName`, no `takes` — so the throw was guaranteed, and
 * `<Wait>` swallowed it into a `console.error` and carried on polling. The visible symptom was
 * nothing at all; the invisible one was that the field which decides how the session ends, `path`,
 * never reached the screen that renders the ending.
 */
export function readComplete(v: unknown): CompleteResponse {
  const o = obj(v, "complete");
  return {
    sessionId: str(o, "sessionId", "complete"),
    path: enumOf(o, "path", ["reveal", "deferred"] as const, "complete"),
    state: enumOf(o, "state", SESSION_STATES, "complete"),
    claimUrl: str(o, "claimUrl", "complete"),
    saved: num(o, "saved", "complete"),
    planned: num(o, "planned", "complete"),
    revealBudgetMs: optNum(o, "revealBudgetMs", "complete"),
    deferredBecause: optStr(o, "deferredBecause", "complete"),
    health: readHealth(o["health"]),
  };
}

// ─── the calls ───────────────────────────────────────────────────────────────

export const api = {
  /** **VOX-LAW 20: the only route that sets a cookie.** Everything else rides on it. */
  session: (signal?: AbortSignal) => request("/session", readSessionEnvelope, { signal }),

  /** §14's dependency report. `/status` is the page; `/health` is liveness and local only (VOX-LAW 28). */
  status: (signal?: AbortSignal) => request("/status", readStatus, { signal }),

  createSession: (body: CreateSessionRequest) =>
    request("/sessions", readCreateSession, { method: "POST", body }),

  /**
   * ⚠️ **`:id` is a `ses_` session id and nothing else.** A claim token here is a 404 — the server
   * shape-checks the prefix before it queries. Somebody arriving on a different computer weeks later
   * with nothing but a link goes through `resolveSession()`, which knows the order.
   */
  sessionView: (id: string, signal?: AbortSignal, claim?: string | null) =>
    request(`/sessions/${encodeURIComponent(id)}`, readSessionView, { signal, claim }),

  /** Answers a `CompleteResponse`, **not** a `SessionView` — see `readComplete`. */
  completeSession: (sessionId: string, claim?: string | null) =>
    request(`/sessions/${encodeURIComponent(sessionId)}/complete`, readComplete, { method: "POST", claim }),

  links: (sessionId: string, signal?: AbortSignal, claim?: string | null) =>
    request(`/sessions/${encodeURIComponent(sessionId)}/links`, readLinks, { signal, claim }),

  reveal: (sessionId: string, signal?: AbortSignal, claim?: string | null) =>
    request(`/sessions/${encodeURIComponent(sessionId)}/reveal`, readReveal, { signal, claim }),

  /** §3.13's "no — delete that". It deletes the audio and the row, immediately. */
  deleteReveal: (sessionId: string, claim?: string | null) =>
    request(`/sessions/${encodeURIComponent(sessionId)}/reveal`, nothing, { method: "DELETE", claim }),

  /**
   * §3.3's one recovery from the denied-microphone screen. Writes a durable row **and** answers the
   * helper's own link.
   *
   * ⚠️ **`liveUrl` is the return value because nothing here can deliver anything.** The old button
   * published an SSE event into a bus nobody was reading and the screen then said *"Sent."* — see
   * `FAMILY.mic.deniedSent` for the full version of why that was the worst sentence in the product.
   * What the client does with this is put it on her clipboard and on her screen.
   */
  askForHelp: (sessionId: string, where: string, detail: string, claim?: string | null) =>
    request(
      `/sessions/${encodeURIComponent(sessionId)}/help`,
      (v) => ({ liveUrl: optStr(obj(v, "help"), "liveUrl", "help") }),
      { method: "POST", body: { where, detail }, claim },
    ),

  /**
   * §3.13's ONE INPUT, and the reason the payoff is reachable at all. **The helper's screen only.**
   *
   * ⚠️ Deliberately not folded into `sessionView`: `SessionView` is what the STUDIO polls, and §3.13
   * is that *"she never sees it"*. A separate call is a separate decision, and `pages/Live.tsx` is the
   * only file in the client that makes it.
   */
  revealText: (sessionId: string, signal?: AbortSignal, claim?: string | null) =>
    request(
      `/sessions/${encodeURIComponent(sessionId)}/reveal-text`,
      (v) => {
        const o = obj(v, "revealText");
        return { revealText: optStr(o, "revealText", "revealText"), revealed: bool(o, "revealed", "revealText") };
      },
      { signal, claim },
    ),

  setRevealText: (sessionId: string, revealText: string, claim?: string | null) =>
    request(
      `/sessions/${encodeURIComponent(sessionId)}/reveal-text`,
      (v) => {
        const o = obj(v, "revealText");
        return {
          revealText: optStr(o, "revealText", "revealText"),
          /** `speaking` | `stored-early` | `stored-no-rev` | `already-revealed` — see the route. */
          outcome: str(o, "outcome", "revealText"),
        };
      },
      { method: "PATCH", body: { revealText }, claim },
    ),

  /**
   * ⚠️ **`emailClaimLink` was here and is deliberately gone.** It posted to
   * `/sessions/:id/claim-link/email`; `studio/Studio.tsx` posted to `/sessions/:id/email-link` for
   * the same field on the same screen; the server implements neither, and it cannot, because nothing
   * in this product can send mail — no transport in `KNOWN_KEYS`, no dependency in `package.json`,
   * and §12.4 refuses a variable nothing reads.
   *
   * `FAMILY.keep.linkEmailLabel`'s field is served by `lib/claimLink.ts` instead, which opens her own
   * mail client with the link composed. When a transport exists, the route comes back **here** and
   * both callers change in one place. It is not the deferred notification of §3.14 — that one is the
   * server messaging somebody who has closed the laptop, and it is still P3's.
   */

  voices: (signal?: AbortSignal) =>
    request("/voices", (v) => arr(obj(v, "voices")["voices"], "voices").map((x, i) => readVoiceSummary(x, `voices[${i}]`)), {
      signal,
    }),

  voice: (id: string, signal?: AbortSignal) =>
    request(`/voices/${encodeURIComponent(id)}`, readVoiceDetail, { signal }),

  /**
   * §2.4's other half: anon session voice → owned voice. `canClaim` decides, on the server.
   *
   * ⚠️ **The token rides `X-Vox-Claim`, not the body**, and it used to ride the body. `access.ts`
   * reads a claim token from exactly three places — the path, that header, and `?claim=` — so a token
   * in a JSON body was a fourth channel nobody reads: `claimTokenOf()` returned null, `grants` came
   * back empty, and `canClaim` refused the one caller it exists for, the grandchild holding the link.
   * A credential travelling by a route the authorising layer does not read is indistinguishable from
   * no credential at all, which is the shape of bug that fails silently and correctly-looking.
   */
  claimVoice: (id: string, claimToken?: string) =>
    request(`/voices/${encodeURIComponent(id)}/claim`, readVoiceDetail, { method: "POST", claim: claimToken }),

  /**
   * §14's tuning preview: one sentence, on the owner's own voice, at `PRIORITY.preview`.
   *
   * ⚠️ **It answers a handle, not audio**, and that is the route's hard requirement rather than a
   * convenience — blocking a connection for the length of an admission wait would hold it behind a
   * family's reveal, which is the thing the wait exists to protect. `audioUrl` answers 404 until the
   * bytes exist, so the caller polls it.
   */
  previewVoice: (id: string, text: string) =>
    request(
      `/voices/${encodeURIComponent(id)}/preview`,
      (v) => {
        const o = obj(v, "preview");
        return { jobId: str(o, "jobId", "preview"), audioUrl: str(o, "audioUrl", "preview") };
      },
      { method: "POST", body: { text } },
    ),

  /** §9.2 — into the fleet manifest. `canPublish` decides, and VOX-LAW 22 lives in that predicate. */
  publishVoice: (id: string) =>
    request(`/voices/${encodeURIComponent(id)}/publish`, readVoiceDetail, { method: "POST" }),

  // ── auth. Vendored shape from audiobook (§11.1): one screen, two modes, invite-gated signup. ──
  signIn: (who: string, password: string) =>
    request("/auth/login", readSessionEnvelope, { method: "POST", body: { who, password } }),

  signUp: (body: { email: string; handle: string; password: string; invite: string }) =>
    request("/auth/signup", readSessionEnvelope, { method: "POST", body }),

  signOut: () => request("/auth/logout", nothing, { method: "POST" }),
};

// ─── arriving with nothing but a link ────────────────────────────────────────

/**
 * `GET /c/:claimToken` with `Accept: application/json` — the session behind a claim token.
 *
 * ⚠️ **This is the ONLY way a claim token becomes a session, and an earlier version of this file was
 * wrong about that.** It claimed the token could be presented "as the id" to `GET /sessions/:id`,
 * because `vox_` and `ses_` are distinguishable by prefix. The server does not do that:
 * `sessionById()` in `server/src/access.ts` opens with `if (!isId("session", id)) return null`, so
 * `GET /api/v1/sessions/vox_…` is a 404 — and `<Claim>` and `<Live>` were both built on the
 * assumption, which made every link this product asks a family to keep resolve to "we couldn't find
 * that" on a session that was sitting on disk the whole time.
 *
 * It should stay the only way. `/c/:claimToken` is the one route that answers with a credential in
 * its URL, and it is hardened for it: `no-store`, `referrer-policy: no-referrer`, `x-robots-tag`,
 * and a 404 that does not distinguish "never existed" from "revoked". A second entry point taking
 * the same credential is a second place to get all four of those right.
 */
export function claimView(claimToken: string, signal?: AbortSignal): Promise<ClaimView> {
  return request(`/c/${encodeURIComponent(claimToken)}`, readClaimView, { signal, root: true });
}

export function readClaimView(v: unknown): ClaimView {
  const o = obj(v, "claim");
  return {
    session: readSessionView(o["session"]),
    voice: o["voice"] === null || o["voice"] === undefined ? null : readVoiceSummary(o["voice"], "claim.voice"),
    claimToken: str(o, "claimToken", "claim"),
  };
}

/**
 * One path segment — a `ses_` session id or a `vox_` claim token — resolved to a session, plus the
 * token to present on every later call.
 *
 * ── The order is the whole point, and it is the server's, not a preference ──────────────────────
 *
 * `shared/access.ts`'s first guard means a claim token **alone** opens `/c/:claimToken` and nothing
 * else. The helper watching this session is on another laptop: no cookie for it, not its actor. So
 * the sequence is fixed:
 *
 *   1. `GET /api/v1/session` — mints him a guest. `lib/session.tsx` has already done this at boot,
 *      which is why there is no call for it here; without it, step 3 is a 404 no matter what.
 *   2. `GET /c/:token` — the token is the credential, and this is the route that takes it.
 *   3. everything else, with the token in `x-vox-claim`.
 *
 * A `ses_` id skips straight to step 3 with no token: that is the person who recorded the session,
 * arriving on her own cookie.
 */
export async function resolveSession(
  idOrClaimToken: string,
  signal?: AbortSignal,
): Promise<{ session: SessionView; claimToken: string | null }> {
  if (idKindOf(idOrClaimToken) === "claim") {
    const view = await claimView(idOrClaimToken, signal);
    return { session: view.session, claimToken: view.claimToken };
  }
  return { session: await api.sessionView(idOrClaimToken, signal), claimToken: null };
}

// ─── the live stream (§3.15) ─────────────────────────────────────────────────

/**
 * `GET /api/v1/sessions/:id/live` — SSE, read-only, one writer, one reader, no clock.
 *
 * ⚠️ **Read-only is enforced by there being no writer here at all**, not by a flag: `EventSource` is
 * a one-way transport and this module exposes no way to send anything on it. §3.15 says the helper
 * "cannot drive her session", and the cheapest way to guarantee that is to use a protocol that
 * cannot. A WebSocket with a client-side `readOnly` boolean would be one merge away from being
 * wrong, on the screen Karti is watching while his mother records.
 *
 * `withCredentials` so the cookie rides along: `readAccess` on the server decides whether this
 * helper may watch, and it refuses 404 (VOX-LAW 18).
 *
 * ⚠️ **The claim token goes in the query string here, and only here.** Everywhere else it travels as
 * `x-vox-claim`, because a query parameter survives into referrers and logs. `EventSource` cannot
 * send a header — there is no options bag for it and no polyfill worth shipping for one route — and
 * the cookie alone is not enough: the helper is a guest who is not this session's actor, so without
 * the token `readAccess` refuses and the stream 404s with the page looking merely quiet. The server
 * already reads it (`CLAIM_QUERY` in `server/src/access.ts`), and `/live` is `no-store`.
 */
export function openLive(sessionId: string, claimToken?: string | null): EventSource {
  const query = claimToken ? `?claim=${encodeURIComponent(claimToken)}` : "";
  return new EventSource(`${BASE}/sessions/${encodeURIComponent(sessionId)}/live${query}`, {
    withCredentials: true,
  });
}

const LIVE_KINDS = ["state", "prompt", "level", "coach", "take", "drain", "error", "ended"] as const;

/**
 * One SSE `data:` line → a `LiveEvent`, or a throw.
 *
 * Same VOX-LAW 14 discipline as everything above, and it earns it here more than anywhere: `/live`'s
 * whole job is to let Karti tell a denied microphone from a stalled upload from a cue she did not
 * hear. An event that half-parses into `{t: "level", peakDbfs: NaN}` draws a flat meter, which is
 * indistinguishable on screen from a microphone that is not working — the exact confusion the screen
 * was built to end.
 */
export function readLiveEvent(data: string): LiveEvent {
  const o = obj(JSON.parse(data), "event");
  const t = enumOf(o, "t", LIVE_KINDS, "event");
  const at = num(o, "at", "event");
  switch (t) {
    case "state":
      return { t, state: enumOf(o, "state", SESSION_STATES, "event"), at };
    case "prompt":
      return { t, promptId: str(o, "promptId", "event"), index: num(o, "index", "event"), total: num(o, "total", "event"), at };
    case "level":
      return { t, peakDbfs: num(o, "peakDbfs", "event"), shortTermLufs: num(o, "shortTermLufs", "event"), at };
    case "coach":
      return { t, line: str(o, "line", "event"), spent: num(o, "spent", "event"), budget: num(o, "budget", "event"), at };
    case "take":
      return { t, take: readTake(o["take"], "event.take"), at };
    case "drain":
      return { t, saved: num(o, "saved", "event"), total: num(o, "total", "event"), at };
    case "error":
      // The one place in the product where a stack trace is the right answer (§3.15). `optStr` and
      // not `str`: a thrown non-Error has no stack, and an event that refused to parse because a
      // stack was missing would drop the very report it exists to carry.
      return { t, where: str(o, "where", "event"), message: str(o, "message", "event"), stack: optStr(o, "stack", "event"), at };
    case "ended":
      return { t, state: enumOf(o, "state", SESSION_STATES, "event"), at };
  }
}

// ─── for the console only ────────────────────────────────────────────────────

/**
 * Full engineering detail for `/live` and `/status`. **Never called from a family-facing page.**
 *
 * VOX-LAW 16b's two ends in one place: this function is the Karti end, and the only correct family
 * end is a string from `shared/voice/copy.ts`. If you are about to render this into a page that a
 * subject can see, the thing you actually want is `FAMILY.mishap.keepGoing`.
 */
export function describeForConsole(err: unknown): { where: string; message: string; stack: string | null } {
  if (err instanceof ApiError) {
    return { where: `api ${err.status} ${err.code}`, message: err.detail, stack: err.stack ?? null };
  }
  if (err instanceof WireError) {
    return { where: "wire", message: err.message, stack: err.stack ?? null };
  }
  if (err instanceof Error) {
    return { where: err.name, message: err.message, stack: err.stack ?? null };
  }
  return { where: "unknown", message: String(err), stack: null };
}

export type { BrowserMetrics, SessionState, SeedKind, SessionShape };
